import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Input, cx } from './index';

/**
 * LOOKUP — escolher digitando, quando a lista é grande demais para rolar
 *
 * Nasceu do seletor de cidades. Um `<select>` com os 853 municípios de Minas
 * é tecnicamente correto e inútil na prática: quem procura "Águas Formosas"
 * rola uma lista de oitocentos nomes, ou digita "a" e o navegador o joga para
 * "Abadia" — o salto por primeira letra do `<select>` nativo não procura, só
 * pula.
 *
 * Aqui a pessoa digita qualquer pedaço do nome e a lista responde.
 *
 * TRÊS DECISÕES QUE NÃO SÃO ENFEITE
 *
 * 1. **A BUSCA IGNORA ACENTO.** Ninguém digita "Águas" com o acento no meio de
 *    um filtro, e "aguas" precisa achar "Águas Formosas". Sem isso o campo
 *    parece quebrado exatamente nas cidades cujo nome é difícil de digitar,
 *    que são as que mais precisam de busca.
 *
 * 2. **A LISTA RENDERIZADA É CURTA.** Abrir 853 opções no DOM trava a
 *    digitação em celular. Mostra as primeiras e diz quantas ficaram de fora —
 *    e quem vê "mais 800" entende que precisa escrever mais, em vez de achar
 *    que a cidade sumiu.
 *
 * 3. **O TECLADO FUNCIONA.** Setas, Enter, Escape, Home e End. Um campo de
 *    busca que só responde ao mouse exclui quem preenche formulário no
 *    teclado — e é a maioria de quem preenche muitos.
 */

export type LookupOption = {
  /** O que volta para quem usa o componente. */
  value: string;

  /** O que a pessoa lê e procura. */
  label: string;

  /** Segunda linha, menor. Ex.: "sem anúncios", "12 anúncios". */
  hint?: string;
};

type Props = {
  options: readonly LookupOption[];
  value: string;
  onChange: (value: string | null) => void;

  /** Texto do campo vazio. */
  placeholder?: string;

  /** Rótulo acessível — o visual costuma vir de fora, num `<label>`. */
  'aria-label'?: string;

  disabled?: boolean;

  /** Mostrado quando a busca não acha nada. */
  emptyMessage?: string;

  /** Quantas opções desenhar por vez. */
  maxVisible?: number;

  id?: string;
};

/**
 * Minúsculo, sem acento.
 *
 * `NFD` decompõe a letra acentuada em letra + marca, e a faixa `0300-036f`
 * é onde as marcas moram. No navegador isso funciona — ao contrário do
 * serviço em C#, que compila sem ICU e precisa de uma tabela à mão.
 */
function comparavel(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function Lookup({
  options,
  value,
  onChange,
  placeholder = 'Digite para procurar',
  disabled = false,
  emptyMessage = 'Nada encontrado.',
  maxVisible = 50,
  id,
  ...rest
}: Props) {
  const gerado = useId();
  const campoId = id ?? `lookup-${gerado}`;
  const listaId = `${campoId}-lista`;

  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [ativo, setAtivo] = useState(0);

  const caixa = useRef<HTMLDivElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);

  const selecionada = options.find((opcao) => opcao.value === value) ?? null;

  const filtradas = useMemo(() => {
    const termo = comparavel(busca);
    if (termo === '') return options;

    /*
      QUEM COMECA COM O TERMO VEM PRIMEIRO.

      A busca e por trecho, e tem que ser: quem digita "formosas" precisa
      achar "Aguas Formosas". So que o trecho sozinho embaralha a lista —
      digitar "aguas" trazia "Cataguases" no meio das "Aguas", e a cidade
      procurada ficava atras de uma que so tem as letras por coincidencia.

      Duas passadas, e nao uma ordenacao: preserva a ordem alfabetica dentro
      de cada grupo, que e a que a pessoa espera ver.
    */
    const comeca: LookupOption[] = [];
    const contem: LookupOption[] = [];

    for (const opcao of options) {
      const alvo = comparavel(opcao.label);
      if (alvo.startsWith(termo)) comeca.push(opcao);
      else if (alvo.includes(termo)) contem.push(opcao);
    }

    return [...comeca, ...contem];
  }, [options, busca]);

  const visiveis = filtradas.slice(0, maxVisible);
  const escondidas = filtradas.length - visiveis.length;

  // Fecha ao clicar fora. Sem isto a lista fica aberta sobre o resto da
  // página enquanto a pessoa tenta usar outro filtro.
  useEffect(() => {
    if (!aberto) return;

    const aoClicar = (evento: MouseEvent) => {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    };

    document.addEventListener('mousedown', aoClicar);

    return () => document.removeEventListener('mousedown', aoClicar);
  }, [aberto]);

  // Mantém a opção ativa visível quando se navega pelo teclado.
  useEffect(() => {
    if (!aberto) return;
    listaRef.current?.querySelector('[data-ativo="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [ativo, aberto]);

  const escolher = (opcao: LookupOption | null) => {
    onChange(opcao ? opcao.value : null);
    setBusca('');
    setAberto(false);
  };

  const abrir = () => {
    if (disabled) return;
    setAberto(true);
    setAtivo(0);
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();

      if (!aberto) { abrir(); return; }

      const passo = evento.key === 'ArrowDown' ? 1 : -1;
      setAtivo((atual) => {
        const proximo = atual + passo;
        if (proximo < 0) return visiveis.length - 1;
        if (proximo >= visiveis.length) return 0;
        return proximo;
      });
      return;
    }

    if (evento.key === 'Home' && aberto) { evento.preventDefault(); setAtivo(0); return; }
    if (evento.key === 'End' && aberto) { evento.preventDefault(); setAtivo(visiveis.length - 1); return; }

    if (evento.key === 'Enter' && aberto) {
      evento.preventDefault();
      const alvo = visiveis[ativo];
      if (alvo) escolher(alvo);
      return;
    }

    if (evento.key === 'Escape') {
      /*
        Escape fecha a lista e NÃO limpa a escolha.

        Limpar seria destrutivo por acidente: Escape é o que se aperta para
        "sair daqui", não para "desfazer o que eu já tinha escolhido".
      */
      setAberto(false);
      setBusca('');
    }
  };

  return (
    <div ref={caixa} className="relative">
      <div className="relative">
        <Input
          id={campoId}
          type="text"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={listaId}
          aria-autocomplete="list"
          aria-activedescendant={aberto && visiveis[ativo] ? `${listaId}-${ativo}` : undefined}
          autoComplete="off"
          disabled={disabled}
          className="w-full pr-9"
          placeholder={selecionada ? selecionada.label : placeholder}
          value={aberto ? busca : (selecionada?.label ?? '')}
          onChange={(evento) => {
            setBusca(evento.target.value);
            setAtivo(0);
            if (!aberto) setAberto(true);
          }}
          onFocus={abrir}
          onKeyDown={aoTeclar}
          {...rest}
        />

        {selecionada && !disabled ? (
          <button
            type="button"
            onClick={() => escolher(null)}
            aria-label="Limpar seleção"
            className="absolute inset-y-0 right-0 grid w-9 place-items-center text-ink-faint hover:text-ink"
          >
            ×
          </button>
        ) : (
          <span aria-hidden="true" className="absolute inset-y-0 right-0 grid w-9 place-items-center text-ink-faint">
            ▾
          </span>
        )}
      </div>

      {aberto ? (
        <ul
          ref={listaRef}
          id={listaId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full list-none overflow-y-auto rounded-(--radius-control) border border-line bg-surface p-1 shadow-(--shadow-lift)"
        >
          {visiveis.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-faint">{emptyMessage}</li>
          ) : null}

          {visiveis.map((opcao, indice) => (
            <li key={opcao.value}>
              <button
                type="button"
                id={`${listaId}-${indice}`}
                role="option"
                aria-selected={opcao.value === value}
                data-ativo={indice === ativo}
                /*
                  `onMouseDown` e não `onClick`: o clique só chega depois do
                  `blur` do campo, e o `blur` já teria fechado a lista — o
                  clique cairia no vazio.
                */
                onMouseDown={(evento) => {
                  evento.preventDefault();
                  escolher(opcao);
                }}
                onMouseEnter={() => setAtivo(indice)}
                className={cx(
                  'flex w-full flex-col items-start gap-0 rounded-(--radius-control) px-3 py-2 text-left',
                  indice === ativo ? 'bg-surface-muted' : '',
                  opcao.value === value ? 'font-semibold text-primary' : 'text-ink',
                )}
              >
                <span className="text-sm">{opcao.label}</span>
                {opcao.hint ? <span className="text-xs text-ink-faint">{opcao.hint}</span> : null}
              </button>
            </li>
          ))}

          {escondidas > 0 ? (
            <li className="border-t border-line px-3 py-2 text-xs text-ink-faint">
              e mais {escondidas.toLocaleString('pt-BR')} — escreva mais para afinar
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
